import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ArrowLeft, FileText, Plus, Printer, Trash2, X } from "lucide-react";
import { CoverLetterProvider, useCoverLetter } from "features/cover-letter/modal/CoverLetterContext";
import {
  CoverLetterDocument,
  createCoverLetterDocument,
  updateCoverLetterDocument,
} from "features/cover-letter/api/CoverLetterApi";
import { CvCategory, listCategories } from "features/cv-processor/api/CvCategoryApi";
import { ScoreBadge, openPrintWindow } from "features/cv-processor/CvProcessorPage";
import ConfirmModal from "shared/ConfirmModal";

function Spinner({ size = "md" }: { size?: "sm" | "md" }) {
  const cls = size === "sm" ? "w-4 h-4" : "w-6 h-6";
  return (
    <div className={`${cls} border-2 border-primary border-t-transparent rounded-full animate-spin`} />
  );
}

// ---------------------------------------------------------------------------
// Shared print engine — much smaller than CV's since a cover letter is one
// block of prose, not six structured sections. Reuses the generic
// openPrintWindow(html) from cv-processor, which is pipeline-agnostic.
// ---------------------------------------------------------------------------

export function buildCoverLetterPrintHtml(doc: CoverLetterDocument): string {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

  const paragraphs = (doc.content || "")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");

  const dateStr = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${esc(doc.title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap" rel="stylesheet">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  font-family: "EB Garamond", Garamond, "Adobe Garamond Pro", serif;
  font-size: 12pt;
  color: #0f0f0f;
  background: #fff;
  padding: 0.75in;
  line-height: 1.5;
}
.date { margin-bottom: 1.5rem; color: #333; }
p { margin-bottom: 1rem; }
@page { size: auto; margin: 0; }
@media print { body { padding: 0.75in; } }
</style>
</head>
<body>
<div class="date">${esc(dateStr)}</div>
${paragraphs}
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// New cover letter modal
// ---------------------------------------------------------------------------

const newDocSchema = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  categoryId: Yup.number().required("Category is required"),
});

function NewCoverLetterModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (doc: CoverLetterDocument) => void;
}) {
  const [categories, setCategories] = useState<CvCategory[]>([]);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {});
  }, []);

  const formik = useFormik({
    initialValues: { title: "", categoryId: "" as number | "" },
    validationSchema: newDocSchema,
    onSubmit: async (vals, { setSubmitting }) => {
      setApiError("");
      try {
        const doc = await createCoverLetterDocument({
          title: vals.title.trim(),
          category_id: Number(vals.categoryId),
        });
        onCreate(doc);
      } catch (e: any) {
        setApiError(e.response?.data?.detail || "Could not create document");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const activeCategories = categories.filter((c) => c.is_active && !c.is_system && !c.is_skill_family);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-lg bg-surface border border-border rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground font-inter">New Cover Letter</h2>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
                Cover letter title
              </label>
              <input
                type="text"
                {...formik.getFieldProps("title")}
                placeholder="e.g. Backend Developer - Standard"
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
              {formik.touched.title && formik.errors.title && (
                <p className="mt-1 text-xs text-red-400 font-inter">{formik.errors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
                Category <span className="text-red-400">*</span>{" "}
                <span className="text-muted-foreground font-normal">(used to auto-pair with a matching CV)</span>
              </label>
              <select
                name="categoryId"
                value={formik.values.categoryId}
                onChange={(e) => formik.setFieldValue("categoryId", e.target.value === "" ? "" : Number(e.target.value))}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <option value="">— Select a category —</option>
                {activeCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {formik.touched.categoryId && formik.errors.categoryId && (
                <p className="mt-1 text-xs text-red-400 font-inter">{formik.errors.categoryId}</p>
              )}
            </div>

            {apiError && <p className="text-sm text-red-400 font-inter">{apiError}</p>}
          </div>

          <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={formik.isSubmitting}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formik.isSubmitting || !formik.values.title.trim() || formik.values.categoryId === ""}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
            >
              {formik.isSubmitting ? <Spinner size="sm" /> : <Plus className="w-4 h-4" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cover letter list view
// ---------------------------------------------------------------------------

function CoverLetterList() {
  const { docs, docsLoading, docsError, deleting, pendingDelete, openDoc, setShowNewModal, requestDelete, confirmDelete, cancelDelete } = useCoverLetter();
  const [categoryMap, setCategoryMap] = useState<Record<number, string>>({});

  useEffect(() => {
    listCategories()
      .then((cats) => {
        const map: Record<number, string> = {};
        cats.forEach((c) => { map[c.id] = c.name; });
        setCategoryMap(map);
      })
      .catch(() => {});
  }, []);

  return (
    <>
    {pendingDelete && (
      <ConfirmModal
        title="Delete cover letter"
        message={`Delete "${pendingDelete.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirming={deleting === pendingDelete.id}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    )}
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-inter">My Cover Letters</h1>
          <p className="text-sm text-muted-foreground font-inter mt-1">
            Maintain standard cover letters per category. They're tailored automatically for a specific job when enhancing a CV from the Job Board.
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors whitespace-nowrap"
        >
          <Plus className="w-4 h-4" />
          New Cover Letter
        </button>
      </div>

      {docsError && (
        <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-inter">
          {docsError}
        </div>
      )}

      {docsLoading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : docs.length === 0 ? (
        <div className="rounded-2xl border border-border bg-surface/30 py-20 text-center">
          <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground font-inter text-sm">No cover letters yet.</p>
          <button
            onClick={() => setShowNewModal(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create your first cover letter
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-left text-sm font-inter">
            <thead className="bg-surface-muted/80 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium w-28 hidden md:table-cell">Last Score</th>
                <th className="px-4 py-3 font-medium w-40 hidden md:table-cell">Updated</th>
                <th className="px-4 py-3 font-medium w-16 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {docs.map((doc) => (
                <tr
                  key={doc.id}
                  onClick={() => openDoc(doc)}
                  className="bg-surface/30 hover:bg-surface-muted/40 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <p className="text-foreground font-medium">{doc.title}</p>
                    {doc.category_id && categoryMap[doc.category_id] && (
                      <p className="text-xs text-muted-foreground mt-0.5">{categoryMap[doc.category_id]}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {doc.last_score?.overall_score != null ? (
                      <ScoreBadge score={doc.last_score.overall_score} />
                    ) : (
                      <span className="text-xs text-muted-foreground font-inter">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                    {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => requestDelete(doc, e)}
                      disabled={deleting === doc.id}
                      className="p-2 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {deleting === doc.id ? <Spinner size="sm" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Cover letter editor view — single textarea, PATCH-on-blur (same dirty-
// tracking pattern as CV's DocumentEditor, drastically simplified since
// there's only one "section").
// ---------------------------------------------------------------------------

function CoverLetterEditor({ initialDoc, onBack }: { initialDoc: CoverLetterDocument; onBack: () => void }) {
  const [doc, setDoc] = useState<CoverLetterDocument>(initialDoc);
  const [draftContent, setDraftContent] = useState(initialDoc.content || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(doc.title);
  const [categories, setCategories] = useState<CvCategory[]>([]);
  const [savingCategory, setSavingCategory] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {});
  }, []);

  const flashSuccess = (msg: string) => {
    setSaveSuccess(msg);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setSaveSuccess(""), 4000);
  };

  const handleCategoryChange = async (newCategoryId: number | null) => {
    setSavingCategory(true);
    setError("");
    try {
      const updated = await updateCoverLetterDocument(doc.id, { category_id: newCategoryId });
      setDoc(updated);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Could not change category");
    } finally {
      setSavingCategory(false);
    }
  };

  const handlePrint = () => {
    openPrintWindow(buildCoverLetterPrintHtml({ ...doc, content: draftContent }));
  };

  const handleBlur = async () => {
    if (!dirty) return;
    setSaving(true);
    setError("");
    try {
      const updated = await updateCoverLetterDocument(doc.id, { content: draftContent });
      setDoc(updated);
      setDirty(false);
      flashSuccess("Saved!");
    } catch (e: any) {
      setError(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const saveTitle = async () => {
    if (!draftTitle.trim()) return;
    setSaving(true);
    try {
      const updated = await updateCoverLetterDocument(doc.id, { title: draftTitle.trim() });
      setDoc(updated);
    } catch {
      setDraftTitle(doc.title);
    } finally {
      setSaving(false);
      setEditingTitle(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          All Cover Letters
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveTitle();
                    if (e.key === "Escape") { setDraftTitle(doc.title); setEditingTitle(false); }
                  }}
                  autoFocus
                  className="text-2xl font-bold text-foreground font-inter bg-transparent border-b border-primary outline-none flex-1"
                />
                <button onClick={saveTitle} disabled={saving} className="text-xs text-primary hover:text-primary/80 font-inter">
                  Save
                </button>
                <button
                  onClick={() => { setDraftTitle(doc.title); setEditingTitle(false); }}
                  className="text-xs text-muted-foreground hover:text-foreground font-inter"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <h1
                className="text-2xl font-bold text-foreground font-inter cursor-pointer hover:text-primary/80 transition-colors"
                onClick={() => setEditingTitle(true)}
                title="Click to rename"
              >
                {doc.title}
              </h1>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handlePrint}
              title="Export / Print cover letter"
              className="inline-flex items-center gap-2 px-3 py-2 bg-surface-muted hover:bg-surface-muted/70 border border-border text-foreground text-sm font-medium font-inter rounded-xl transition-colors"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-inter shrink-0">Category: <span className="text-red-400">*</span></label>
          <select
            value={doc.category_id ?? ""}
            onChange={(e) => { if (e.target.value !== "") handleCategoryChange(Number(e.target.value)); }}
            disabled={savingCategory}
            className="flex-1 max-w-xs px-2.5 py-1.5 bg-surface-muted border border-border rounded-lg text-xs text-foreground font-inter focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          >
            {!doc.category_id && <option value="">— Select a category —</option>}
            {categories.filter((c) => c.is_active && !c.is_system && !c.is_skill_family).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {!doc.category_id && (
            <span className="text-[10px] text-amber-500/70 font-inter">Required — won't auto-pair with a CV category until set</span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-inter flex items-start gap-2">
          <X className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-inter">
          {saveSuccess}
        </div>
      )}

      {doc.last_score?.overall_score != null && (
        <div className="flex items-center gap-1.5 mb-4">
          <span className="text-xs text-muted-foreground font-inter">Last tailored score</span>
          <ScoreBadge score={doc.last_score.overall_score} size="md" />
        </div>
      )}

      <div className="rounded-2xl border border-border bg-surface/50 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground font-inter">Base content</h2>
          <div className="flex items-center gap-2">
            {saving && <Spinner size="sm" />}
            <button
              onClick={handleBlur}
              disabled={!dirty || saving}
              className="inline-flex items-center px-3 py-1.5 bg-muted hover:bg-border disabled:opacity-30 disabled:cursor-not-allowed text-foreground text-xs font-medium font-inter rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </div>
        <textarea
          value={draftContent}
          onChange={(e) => { setDraftContent(e.target.value); setDirty(true); }}
          onBlur={handleBlur}
          placeholder="Write your standard cover letter here. It'll be tailored to each specific job's description when enhanced from the Job Board."
          rows={18}
          className="w-full px-4 py-3 bg-transparent text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none resize-none"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root page component
// ---------------------------------------------------------------------------

function CoverLetterInner() {
  const { view, activeDoc, showNewModal, setShowNewModal, handleCreate, backToList } = useCoverLetter();

  if (view === "editor" && activeDoc) {
    return <CoverLetterEditor initialDoc={activeDoc} onBack={backToList} />;
  }

  return (
    <>
      <CoverLetterList />
      {showNewModal && (
        <NewCoverLetterModal onClose={() => setShowNewModal(false)} onCreate={handleCreate} />
      )}
    </>
  );
}

export default function CoverLetterPage() {
  return (
    <CoverLetterProvider>
      <CoverLetterInner />
    </CoverLetterProvider>
  );
}
